import { DateTime } from 'luxon';
import { BookingStatus, type JobStatus } from '../../../__generated__/graphql';
import { jobStatusView, type ServiceJob } from '../../containers/ServiceGuestPage/serviceJob';
import { canBookHandover, handoverDeepLink } from '../../utils/handoverLink';
import { resolveBrandRedirectHost } from '../../utils/referralRedirectUrl';
import { getBrandSiteHost } from '../../utils/siteHost';
import { Button } from '../button';
import { Badge, Card, CardBody, CardHeader, Muted, Stack, Strong } from '../ui';

export interface HandoverJobSummary {
  bookingId: string;
  workerName?: string | null;
  status: string;
  jobStatus?: JobStatus | null;
  serviceDate: string;
  windowStartAt: number;
  windowEndAt: number;
  timezone: string;
  brandSite: string;
}

/**
 * A car trip's handover job (spec 019 W27, T067), for the car's host: the job
 * booked on the services site, or "Book a handover" while the trip is booked
 * and not started. The button opens the services site with the car, the trip
 * and a window one hour each side of pickup. Nothing shows without
 * `servicesSite` (the backend's `handoverServicesSite`): no brand sells
 * services yet, so there is nowhere to book one.
 */
export function HandoverJobCard({
  trip,
  servicesSite,
}: {
  servicesSite?: string | null;
  trip: {
    id: string;
    carId?: string | null;
    status?: string | null;
    startAt: number;
    hostFinishedCheckin?: boolean | null;
    timezone: string;
    handoverJob?: HandoverJobSummary | null;
  };
}) {
  const job = trip.handoverJob ?? null;
  const offer = Boolean(trip.carId) && canBookHandover(trip);
  if (!servicesSite || (!job && !offer)) return null;
  const servicesHost = resolveBrandRedirectHost(servicesSite, getBrandSiteHost());

  const view = job
    ? jobStatusView({
        status: job.status as BookingStatus,
        serviceJob: { jobStatus: job.jobStatus ?? null } as ServiceJob['serviceJob'],
      })
    : null;
  const windowLabel = job
    ? `${DateTime.fromMillis(job.windowStartAt, { zone: job.timezone }).toFormat('ccc, LLL d h:mm a')} – ${DateTime.fromMillis(job.windowEndAt, { zone: job.timezone }).toFormat('h:mm a')}`
    : null;

  return (
    <Card data-testid="handover-job">
      <CardHeader
        title="Handover"
        description="A worker meets the guest, hands over the car and sends you photos."
      />
      <CardBody>
        <Stack $gap={4}>
          {job && view && (
            <Stack $gap={4}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Strong>{job.workerName ?? 'Worker'}</Strong>
                <Badge tone={view.tone}>{view.label}</Badge>
              </div>
              <Muted $size="sm">{windowLabel}</Muted>
            </Stack>
          )}
          {offer && trip.carId && (
            <div>
              <Button
                type="button"
                theme="outlined"
                text={job ? 'Book another handover' : 'Book a handover'}
                onClick={() =>
                  window.open(
                    handoverDeepLink(
                      {
                        carId: trip.carId!,
                        bookingId: trip.id,
                        startAt: trip.startAt,
                        timezone: trip.timezone,
                      },
                      getBrandSiteHost(),
                      servicesHost,
                    ),
                    '_blank',
                    'noopener',
                  )
                }
                dataTestId="handover-book"
              />
            </div>
          )}
        </Stack>
      </CardBody>
    </Card>
  );
}
